import { useMemo, useState } from 'react';
import { Pressable, ScrollView, Text, TextInput, View, useWindowDimensions } from 'react-native';
import {
  AlertTriangle,
  ArrowDownToLine,
  ClipboardCheck,
  FileText,
  History,
  PackageX,
  Pencil,
  Plus,
  Search,
  Trash2,
} from 'lucide-react-native';

import { Badge, Button, Card, EmptyState, SkeletonRows } from '@/components/ui';
import { useFirestoreCollection } from '@/hooks/useFirestoreCollection';
import {
  eliminarFormulario,
  formulariosQuery,
  usosFormularioQuery,
} from '@/services/firebase/formularios';
import { useAuth, selectIsAdmin } from '@/stores/authStore';
import { colors, radius, typography } from '@/theme/tokens';
import { formatDate } from '@/utils/format';
import type { Formulario } from '@/types/domain';

import { FormularioSheet } from '@/components/formularios/FormularioSheet';
import { UsarFormularioSheet } from '@/components/formularios/UsarFormularioSheet';
import { IngresarFormularioSheet } from '@/components/formularios/IngresarFormularioSheet';

type Filtro = 'todos' | 'bajo' | 'agotado';

export function FormulariosScreen() {
  const { width } = useWindowDimensions();
  const wide = width >= 920;

  const { data: formularios, loading } = useFirestoreCollection(formulariosQuery);
  const isAdmin = useAuth(selectIsAdmin);

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filtro>('todos');
  const [editing, setEditing] = useState<Formulario | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [usarTarget, setUsarTarget] = useState<Formulario | null>(null);
  const [ingresarTarget, setIngresarTarget] = useState<Formulario | null>(null);
  const [historialTarget, setHistorialTarget] = useState<Formulario | null>(null);

  const filtered = useMemo(() => {
    let arr = formularios;
    if (filter === 'bajo') arr = arr.filter((f) => f.stockBajo && f.stock > 0);
    else if (filter === 'agotado') arr = arr.filter((f) => f.stock === 0);
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter(
        (f) => f.nombre.toLowerCase().includes(q) || (f.descripcion ?? '').toLowerCase().includes(q),
      );
    }
    return arr;
  }, [formularios, search, filter]);

  const stats = useMemo(() => {
    const total = formularios.length;
    const bajos = formularios.filter((f) => f.stockBajo && f.stock > 0).length;
    const agotados = formularios.filter((f) => f.stock === 0).length;
    const totalEjemplares = formularios.reduce((sum, f) => sum + (f.stock ?? 0), 0);
    return { total, bajos, agotados, totalEjemplares };
  }, [formularios]);

  return (
    <ScrollView contentContainerStyle={{ padding: wide ? 28 : 16, gap: 18, paddingBottom: 80 }}>
      {/* Hero */}
      <View
        style={{
          padding: wide ? 24 : 18,
          borderRadius: radius['3xl'],
          backgroundColor: colors.magenta[700],
          // @ts-ignore web only
          backgroundImage: `radial-gradient(800px 300px at 110% -10%, ${colors.magenta[500]}88, transparent 60%), linear-gradient(135deg, ${colors.magenta[800]} 0%, ${colors.magenta[600]} 100%)`,
          overflow: 'hidden',
        }}
      >
        <View style={{ flexDirection: wide ? 'row' : 'column', alignItems: wide ? 'center' : 'flex-start', gap: 18 }}>
          <View style={{ flex: 1, gap: 4 }}>
            <Text
              style={{
                color: 'rgba(255,255,255,0.85)',
                fontSize: 11,
                fontWeight: typography.weights.bold,
                letterSpacing: 1.4,
                textTransform: 'uppercase',
              }}
            >
              Formularios preimpresos
            </Text>
            <Text
              style={{
                color: colors.white,
                fontFamily: typography.display.fontFamily,
                fontSize: wide ? 36 : 28,
                fontWeight: typography.weights.bold,
                letterSpacing: -0.6,
              }}
            >
              {stats.totalEjemplares} ejemplares en stock
            </Text>
            <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>
              Castración, autorizaciones, vacuna antirrábica, certificados.
            </Text>
          </View>
          {isAdmin ? (
            <Pressable
              onPress={() => {
                setEditing(null);
                setEditOpen(true);
              }}
              style={({ hovered }) => ({
                flexDirection: 'row',
                alignItems: 'center',
                gap: 8,
                paddingHorizontal: 18,
                height: 44,
                borderRadius: radius.xl,
                backgroundColor: hovered ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.12)',
                borderWidth: 1,
                borderColor: 'rgba(255,255,255,0.2)',
                // @ts-ignore web only
                cursor: 'pointer',
              })}
            >
              <Plus size={16} color={colors.white} />
              <Text style={{ color: colors.white, fontWeight: typography.weights.bold, fontSize: 13 }}>
                Nuevo formulario
              </Text>
            </Pressable>
          ) : null}
        </View>
      </View>

      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
        <MiniChip
          label="Tipos"
          value={stats.total}
          color={colors.magenta[700]}
          active={filter === 'todos'}
          onPress={() => setFilter('todos')}
        />
        <MiniChip
          label="Stock bajo"
          value={stats.bajos}
          color={colors.warning[600]}
          active={filter === 'bajo'}
          onPress={() => setFilter('bajo')}
        />
        <MiniChip
          label="Agotados"
          value={stats.agotados}
          color={colors.danger[600]}
          active={filter === 'agotado'}
          onPress={() => setFilter('agotado')}
        />
      </View>

      <Card>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <View
            style={{
              flex: 1,
              minWidth: 220,
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              backgroundColor: colors.crema[50],
              borderRadius: radius.full,
              paddingHorizontal: 14,
              height: 42,
              borderWidth: 1,
              borderColor: colors.ink[100],
            }}
          >
            <Search size={16} color={colors.ink[400]} />
            <TextInput
              placeholder="Buscar formulario…"
              placeholderTextColor={colors.ink[400]}
              value={search}
              onChangeText={setSearch}
              style={{
                flex: 1,
                fontSize: 14,
                color: colors.ink[900],
                fontFamily: typography.body.fontFamily,
                // @ts-ignore web only
                outlineStyle: 'none',
              }}
            />
          </View>
        </View>
      </Card>

      {loading ? (
        <SkeletonRows rows={4} />
      ) : filtered.length === 0 ? (
        <Card>
          <EmptyState
            illustration={search || filter !== 'todos' ? 'note' : 'shelf'}
            title={search || filter !== 'todos' ? 'Sin resultados' : 'Aún no hay formularios cargados'}
            message={
              search || filter !== 'todos'
                ? 'Probá con otro término o tocá «Tipos cargados» para quitar filtros.'
                : isAdmin
                  ? 'Tocá «Nuevo formulario» para empezar.'
                  : 'La administradora cargará los formularios disponibles.'
            }
            actionLabel={isAdmin && !(search || filter !== 'todos') ? 'Nuevo formulario' : undefined}
            onAction={
              isAdmin && !(search || filter !== 'todos')
                ? () => {
                    setEditing(null);
                    setEditOpen(true);
                  }
                : undefined
            }
          />
        </Card>
      ) : (
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 14 }}>
          {filtered.map((f) => (
            <FormularioCard
              key={f.id}
              formulario={f}
              wide={wide}
              isAdmin={isAdmin}
              onUsar={() => setUsarTarget(f)}
              onIngresar={() => setIngresarTarget(f)}
              onEditar={() => {
                setEditing(f);
                setEditOpen(true);
              }}
              onEliminar={() => {
                if (typeof window !== 'undefined' && !window.confirm(`¿Eliminar el formulario "${f.nombre}"?`)) return;
                eliminarFormulario(f.id).catch((e) => alert(e?.message ?? 'No se pudo eliminar.'));
              }}
              onHistorial={() => setHistorialTarget(f)}
            />
          ))}
        </View>
      )}

      <FormularioSheet visible={editOpen} onClose={() => setEditOpen(false)} formulario={editing} />
      <UsarFormularioSheet visible={!!usarTarget} onClose={() => setUsarTarget(null)} formulario={usarTarget} />
      <IngresarFormularioSheet visible={!!ingresarTarget} onClose={() => setIngresarTarget(null)} formulario={ingresarTarget} />
      <HistorialUsos
        visible={!!historialTarget}
        onClose={() => setHistorialTarget(null)}
        formulario={historialTarget}
      />
    </ScrollView>
  );
}

// ----------------------------------------------------------------------------
// Mini chip compacto reemplaza StatTile
// ----------------------------------------------------------------------------
function MiniChip({
  label,
  value,
  color,
  active,
  onPress,
}: {
  label: string;
  value: number;
  color: string;
  active?: boolean;
  onPress?: () => void;
}) {
  return (
    <Pressable
      onPress={onPress}
      style={({ hovered }) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        paddingHorizontal: 12,
        height: 36,
        borderRadius: 999,
        backgroundColor: active ? color : hovered ? colors.crema[100] : colors.crema[50],
        borderWidth: 1.5,
        borderColor: active ? color : colors.ink[100],
        // @ts-ignore web only
        cursor: 'pointer',
      })}
    >
      <Text style={{ fontSize: 12, fontWeight: typography.weights.bold, color: active ? colors.white : colors.ink[700] }}>
        {label}
      </Text>
      <View
        style={{
          paddingHorizontal: 8,
          height: 22,
          borderRadius: 999,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: active ? 'rgba(255,255,255,0.25)' : color,
        }}
      >
        <Text style={{ fontSize: 12, fontWeight: typography.weights.bold, color: colors.white }}>
          {value}
        </Text>
      </View>
    </Pressable>
  );
}

// ----------------------------------------------------------------------------
// Card individual
// ----------------------------------------------------------------------------
function FormularioCard({
  formulario,
  wide,
  isAdmin,
  onUsar,
  onIngresar,
  onEditar,
  onEliminar,
  onHistorial,
}: {
  formulario: Formulario;
  wide: boolean;
  isAdmin: boolean;
  onUsar: () => void;
  onIngresar: () => void;
  onEditar: () => void;
  onEliminar: () => void;
  onHistorial: () => void;
}) {
  const bajo = formulario.stockBajo && formulario.stock > 0;
  const agotado = formulario.stock === 0;
  const stockColor = agotado ? colors.danger[600] : bajo ? colors.warning[600] : colors.success[600];

  return (
    <View
      style={{
        width: wide ? '32%' : '100%',
        minWidth: 260,
        backgroundColor: colors.white,
        borderRadius: radius['2xl'],
        borderWidth: 1,
        borderColor: colors.ink[100],
        padding: 16,
        gap: 12,
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 12 }}>
        <View
          style={{
            width: 40,
            height: 40,
            borderRadius: 12,
            backgroundColor: colors.magenta[100],
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <FileText size={18} color={colors.magenta[700]} />
        </View>
        <View style={{ flex: 1 }}>
          <Text
            numberOfLines={2}
            style={{ fontSize: 15, fontWeight: typography.weights.bold, color: colors.ink[900] }}
          >
            {formulario.nombre}
          </Text>
          {formulario.descripcion ? (
            <Text numberOfLines={2} style={{ fontSize: 12, color: colors.ink[500], marginTop: 2 }}>
              {formulario.descripcion}
            </Text>
          ) : null}
        </View>
      </View>

      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 12,
          borderRadius: radius.xl,
          backgroundColor: colors.crema[50],
        }}
      >
        <View>
          <Text style={{ fontSize: 10, fontWeight: typography.weights.bold, color: colors.ink[500], letterSpacing: 1, textTransform: 'uppercase' }}>
            En stock
          </Text>
          <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 26, fontWeight: typography.weights.bold, color: stockColor }}>
            {formulario.stock}
          </Text>
          <Text style={{ fontSize: 11, color: colors.ink[500] }}>mín. {formulario.stockMin}</Text>
        </View>
        <View style={{ alignItems: 'flex-end', gap: 4 }}>
          {agotado ? (
            <Badge label="Agotado" tone="danger" />
          ) : bajo ? (
            <Badge label="Stock bajo" tone="warning" />
          ) : (
            <Badge label="Disponible" tone="success" />
          )}
          {formulario.ubicacion ? (
            <Text style={{ fontSize: 11, color: colors.ink[500], textAlign: 'right', maxWidth: 130 }} numberOfLines={2}>
              {formulario.ubicacion}
            </Text>
          ) : null}
        </View>
      </View>

      <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
        <Button
          label="Usar"
          variant="success"
          size="sm"
          iconLeft={<ClipboardCheck size={14} color={colors.white} />}
          onPress={onUsar}
          disabled={agotado}
        />
        {isAdmin ? (
          <Button
            label="Ingresar"
            variant="primary"
            size="sm"
            iconLeft={<ArrowDownToLine size={14} color={colors.white} />}
            onPress={onIngresar}
          />
        ) : null}
        <Button
          label="Historial"
          variant="ghost"
          size="sm"
          iconLeft={<History size={14} color={colors.ink[700]} />}
          onPress={onHistorial}
        />
        {isAdmin ? (
          <>
            <Pressable
              onPress={onEditar}
              hitSlop={6}
              style={({ hovered }) => ({
                width: 36,
                height: 36,
                borderRadius: radius.xl,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: hovered ? colors.crema[100] : colors.crema[50],
                // @ts-ignore web only
                cursor: 'pointer',
              })}
            >
              <Pencil size={14} color={colors.ink[700]} />
            </Pressable>
            <Pressable
              onPress={onEliminar}
              hitSlop={6}
              style={({ hovered }) => ({
                width: 36,
                height: 36,
                borderRadius: radius.xl,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: hovered ? colors.danger[50] : 'transparent',
                // @ts-ignore web only
                cursor: 'pointer',
              })}
            >
              <Trash2 size={14} color={colors.danger[600]} />
            </Pressable>
          </>
        ) : null}
      </View>
    </View>
  );
}

// ----------------------------------------------------------------------------
// Historial de usos (sheet inline)
// ----------------------------------------------------------------------------
function HistorialUsos({
  visible,
  onClose,
  formulario,
}: {
  visible: boolean;
  onClose: () => void;
  formulario: Formulario | null;
}) {
  const q = useMemo(() => (formulario ? usosFormularioQuery(formulario.id) : null), [formulario]);
  const { data: usos, loading } = useFirestoreCollection(q ?? formulariosQuery);

  if (!visible || !formulario) return null;

  return (
    <View
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.4)',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <Pressable
        onPress={onClose}
        style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
      />
      <View
        style={{
          width: '90%',
          maxWidth: 680,
          maxHeight: '80%',
          backgroundColor: colors.white,
          borderRadius: radius['2xl'],
          overflow: 'hidden',
        }}
      >
        <View
          style={{
            paddingHorizontal: 20,
            paddingVertical: 16,
            backgroundColor: colors.magenta[700],
            flexDirection: 'row',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <History size={18} color={colors.white} />
          <Text style={{ flex: 1, color: colors.white, fontFamily: typography.display.fontFamily, fontSize: 17, fontWeight: typography.weights.bold }}>
            Historial · {formulario.nombre}
          </Text>
          <Pressable
            onPress={onClose}
            style={({ hovered }) => ({
              paddingHorizontal: 12,
              paddingVertical: 6,
              borderRadius: radius.full,
              backgroundColor: hovered ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)',
              // @ts-ignore web only
              cursor: 'pointer',
            })}
          >
            <Text style={{ color: colors.white, fontWeight: typography.weights.bold, fontSize: 12 }}>Cerrar</Text>
          </Pressable>
        </View>

        <ScrollView contentContainerStyle={{ padding: 16, gap: 8 }}>
          {loading ? (
            <SkeletonRows rows={3} />
          ) : (usos as any[]).length === 0 ? (
            <EmptyState
              illustration="note"
              title="Sin movimientos"
              message="Los usos y los ingresos de stock se van a registrar acá."
            />
          ) : (
            (usos as any[]).map((u) => (
              <View
                key={u.id}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  padding: 12,
                  borderRadius: radius.lg,
                  backgroundColor: colors.crema[50],
                  gap: 12,
                }}
              >
                <View
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 10,
                    backgroundColor: u.cantidad > 0 ? colors.success[50] : colors.magenta[100],
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {u.cantidad > 0 ? (
                    <ArrowDownToLine size={16} color={colors.success[600]} />
                  ) : (
                    <ClipboardCheck size={16} color={colors.magenta[700]} />
                  )}
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 13, fontWeight: typography.weights.bold, color: colors.ink[900] }}>
                    {u.cantidad > 0 ? `+${u.cantidad} ingreso` : `${Math.abs(u.cantidad)} usado`}
                  </Text>
                  <Text style={{ fontSize: 12, color: colors.ink[600] }}>
                    {u.motivo ?? '—'}
                  </Text>
                  {u.numeroTalonario || u.solicitadoPor ? (
                    <Text style={{ fontSize: 11, color: colors.ink[500] }}>
                      {u.numeroTalonario ? `Talonario: ${u.numeroTalonario}` : ''}
                      {u.numeroTalonario && u.solicitadoPor ? ' · ' : ''}
                      {u.solicitadoPor ? `Solicitó: ${u.solicitadoPor}` : ''}
                    </Text>
                  ) : null}
                </View>
                <View style={{ alignItems: 'flex-end' }}>
                  <Text style={{ fontSize: 12, color: colors.ink[700] }}>
                    {u.fecha?.toDate ? formatDate(u.fecha.toDate()) : ''}
                  </Text>
                  <Text style={{ fontSize: 11, color: colors.ink[500] }}>{u.autorNombre}</Text>
                </View>
              </View>
            ))
          )}
        </ScrollView>
      </View>
    </View>
  );
}
